import { useState } from 'react';
import { CHART_RANGES, MED_COLORS, MS_PER_DAY, OTHER_MED_COLOR, chartRangeLabel } from '../constants.js';
import { formatDatePart, formatTimePart, formatTimestamp } from '../lib/format.js';

// Per-child timeline: temperature as a line (single °C axis) with medications as
// event markers on the baseline. Hand-rolled SVG — no chart dependency — so it
// themes with dark mode and works offline like the rest of the app.
// subtitle/onCollapse are optional: Records names whose chart it is and lets it
// fold away; the Temperature tab shows it plainly.
export function TemperatureChart({darkMode, temps, meds, subtitle, onCollapse}) {
  // null = follow the data (see autoWindowDays). A button press pins the range
  // and keeps it pinned, so the chart never yanks itself out from under someone
  // who deliberately zoomed out.
  const [pinnedWindow, setPinnedWindow] = useState(null);
  const [detail, setDetail] = useState(null);
  // Absolute right edge of the window while panned, or null to track the
  // present. Absolute rather than an offset from `now`: `now` is recomputed
  // every render, so a relative offset made a panned window creep forward by
  // however much wall-clock time had passed since the click.
  const [pannedEnd, setPannedEnd] = useState(null);

  const now = Date.now();
  const WIDEST_RANGE = CHART_RANGES[CHART_RANGES.length - 1];

  const allTimes = [...temps, ...meds]
    .map(e => new Date(e.timestamp).getTime())
    .filter(t => !isNaN(t));

  // Open on the smallest range that still covers this child's data, so the
  // readings fill the width instead of bunching into one edge. A 36h span needs
  // 48h; 20h needs 24h.
  //
  // Keyed to the OLDEST entry — the span — not the newest. Keying it to the
  // newest was a misreading of "no data past 20h → 24h": that means "nothing
  // logged in the last 20h", and picking by recency collapsed a 36h span down
  // to 12h and cut most of it off. Which child is charted is a separate
  // question, decided by the most *recent* entry (see HistoryView).
  //
  // Only the last WIDEST_RANGE days count towards the span: a single reading
  // from an illness three weeks ago would otherwise pin every view to 7d
  // forever. That older history is still reachable by panning.
  const autoWindowDays = (() => {
    const recent = allTimes.filter(t => t >= now - WIDEST_RANGE * MS_PER_DAY);
    if (recent.length === 0) return 1;
    const spanDays = (now - Math.min(...recent)) / MS_PER_DAY;
    return CHART_RANGES.find(d => d >= spanDays) || WIDEST_RANGE;
  })();

  const windowDays = pinnedWindow ?? autoWindowDays;
  const tEnd = pannedEnd ?? now;
  const tStart = tEnd - windowDays * MS_PER_DAY;
  const isPanned = pannedEnd !== null;

  // Panning jumps to the next record rather than stepping a fixed window, so
  // reaching a reading from three weeks ago is one click instead of ten empty
  // screens. Both buttons disable when there's nothing further that way.
  const olderThanWindow = allTimes.filter(t => t < tStart);
  const newerThanWindow = allTimes.filter(t => t > tEnd);
  const canGoOlder = olderThanWindow.length > 0;

  // Land the target entry just inside the edge it came from, with a tenth of a
  // window of headroom, so it isn't drawn flush against the axis.
  // Never park the window in the future: past `now` it just tracks the present.
  const jumpTo = (endMs) => {
    setPannedEnd(endMs >= now ? null : endMs);
    setDetail(null);
  };
  const goOlder = () => {
    const target = Math.max(...olderThanWindow);
    jumpTo(target + windowDays * MS_PER_DAY * 0.1);
  };
  const goNewer = () => {
    if (newerThanWindow.length === 0) { jumpTo(now); return; }
    const target = Math.min(...newerThanWindow);
    jumpTo(target - windowDays * MS_PER_DAY * 0.1 + windowDays * MS_PER_DAY);
  };

  const pts = temps
    .map(e => ({ t: new Date(e.timestamp).getTime(), v: Number(e.tempC), raw: e }))
    .filter(p => p.t >= tStart && p.t <= tEnd && !isNaN(p.v))
    .sort((a, b) => a.t - b.t);
  const medPts = meds
    .map(e => ({ t: new Date(e.timestamp).getTime(), raw: e }))
    .filter(p => p.t >= tStart && p.t <= tEnd)
    .sort((a, b) => a.t - b.t);

  // Layout inside the viewBox (scales to container width).
  const W = 360, H = 210, L = 30, R = 348, T = 14, B = 168;
  const BADGE_R = 6; // radius of the stacked-marker count badge
  const span = tEnd - tStart;
  const xOf = (t) => L + (t - tStart) / span * (R - L);

  const vals = pts.map(p => p.v);
  const domainMin = Math.min(vals.length ? Math.min(...vals) : 37, 37) - 0.3;
  const domainMax = Math.max(vals.length ? Math.max(...vals) : 39, 38.5) + 0.3;
  const yOf = (v) => B - (v - domainMin) / (domainMax - domainMin) * (B - T);

  const degTicks = [];
  for (let d = Math.ceil(domainMin); d <= Math.floor(domainMax); d++) degTicks.push(d);

  const axis = darkMode ? '#9ca3af' : '#6b7280';
  const grid = darkMode ? '#374151' : '#e5e7eb';
  const TEMP = '#ef4444', FEVER = '#f59e0b';
  const medStyle = (m) =>
    m === 'paracetamol' ? { c: MED_COLORS.paracetamol.hex, s: 'circle' }
    : m === 'ibuprofen' ? { c: MED_COLORS.ibuprofen.hex, s: 'diamond' }
    : { c: darkMode ? OTHER_MED_COLOR.hexDark : OTHER_MED_COLOR.hex, s: 'square' };
  // Tapped-marker label, e.g. "Paracetamol 5.2 ml (160 mg/5 ml)". The strength
  // matters as much as the volume — the same millilitres are a different dose
  // out of a different bottle — so it rides along whenever the entry recorded
  // one. Older entries may not have it, hence the guard.
  const medLabel = (e) => e.medication === 'other'
    ? (e.medicationType || 'Other')
    : `${e.medication.charAt(0).toUpperCase() + e.medication.slice(1)}`
      + (e.dose ? ` ${e.dose} ml` : '')
      + (e.concentration ? ` (${e.concentration})` : '');

  // Every medication marker sits on the same baseline, so two medicines given at
  // once land exactly on top of each other: only the last one drawn is visible,
  // and only it was clickable.
  //
  // Overlap is measured in rendered x, not by comparing timestamps, because
  // that's what makes them one visual point: entries minutes apart collide at
  // 7d but are clearly separate at 12h, so both the hit area and the badge
  // should follow the zoom.
  const MARKER_HIT_RADIUS = 6;

  // One grouping drives both the count badge and the tap detail, so what the
  // badge promises is exactly what a tap delivers. medPts is time-sorted and x
  // increases with time, so a single sweep suffices: each point either joins the
  // run being built or starts a new one.
  const medGroups = medPts.reduce((groups, p) => {
    const x = xOf(p.t);
    const last = groups[groups.length - 1];
    if (last && Math.abs(x - last.x) <= MARKER_HIT_RADIUS) last.items.push(p);
    else groups.push({ x, items: [p] });
    return groups;
  }, []);

  const medDetailAt = (p) => {
    const group = (medGroups.find(g => g.items.includes(p)) || { items: [p] }).items;
    const stamps = [...new Set(group.map(q => formatTimestamp(q.raw.timestamp)))];
    // One shared timestamp reads better than repeating it: everything in the
    // group went in at the same displayed minute, so name it once.
    if (stamps.length === 1) return `${group.map(q => medLabel(q.raw)).join(' + ')} · ${stamps[0]}`;

    // Different minutes of the same day — the common case for a stack. The date
    // is identical on every line, so it's hoisted into a heading and each
    // medicine keeps only its time: "Aug 15: Paracetamol 5 ml (08:46 AM) |
    // Ibuprofen 6 ml (10:16 AM)".
    const days = [...new Set(group.map(q => formatDatePart(q.raw.timestamp)))];
    if (days.length === 1) {
      return `${days[0]}: ` + group
        .map(q => `${medLabel(q.raw)} (${formatTimePart(q.raw.timestamp)})`)
        .join(' | ');
    }

    // A group is grouped by rendered x, not by time, so at wide zooms it can
    // straddle midnight. Then there's no single date to hoist, and each entry
    // keeps its own full stamp rather than being filed under one that isn't
    // true for all of them.
    return group
      .map(q => `${medLabel(q.raw)} (${formatTimestamp(q.raw.timestamp)})`)
      .join(' | ');
  };

  // Time ticks land on round wall-clock values — midnight, 03:00, 09:00, noon,
  // 15:00 — instead of on "now", "now minus half the window". A tick that means
  // "6h 20m ago" is unreadable when you're trying to place a reading against the
  // actual time of day, and it moves every time the chart re-renders. The step
  // widens with the window so the labels never collide.
  const tickStepHours = windowDays <= 0.5 ? 3 : windowDays <= 1 ? 6 : windowDays <= 3 ? 12 : 24;
  const xTicks = (() => {
    const out = [];
    const dayStart = new Date(tStart);
    dayStart.setHours(0, 0, 0, 0); // local midnight at or before the window start
    // Step via setHours rather than adding milliseconds so a DST change doesn't
    // knock every later tick off the hour.
    for (let h = 0; ; h += tickStepHours) {
      const d = new Date(dayStart);
      d.setHours(dayStart.getHours() + h);
      const t = d.getTime();
      if (t > tEnd) break;
      if (t >= tStart) out.push(t);
    }
    return out;
  })();

  const tickLabel = (t) => {
    const d = new Date(t);
    // On multi-day windows midnight carries the day name, which is the more
    // useful anchor there; inside a single day the hour alone is unambiguous.
    if (tickStepHours >= 24 || (d.getHours() === 0 && windowDays > 1)) {
      return d.toLocaleDateString('en-US', { weekday: 'short' });
    }
    return `${String(d.getHours()).padStart(2, '0')}:00`;
  };
  // Keep the first/last labels from hanging off the edge of the viewBox.
  const tickAnchor = (x) => x < L + 12 ? 'start' : x > R - 12 ? 'end' : 'middle';

  const hasOther = medPts.some(p => p.raw.medication === 'other');
  const empty = pts.length === 0 && medPts.length === 0;

  const line = pts.map(p => `${xOf(p.t).toFixed(1)},${yOf(p.v).toFixed(1)}`).join(' ');

  const btn = (d) => `px-2 py-1 rounded text-xs font-medium transition ${
    windowDays === d ? 'bg-indigo-600 text-white' : darkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
  }`;

  return (
    <div className={`border rounded-lg p-4 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-center justify-between mb-3">
        {onCollapse ? (
          <button onClick={onCollapse} aria-label="Hide timeline"
            className={`flex items-center gap-1 font-semibold min-w-0 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            <span className="truncate">Timeline{subtitle && <span className={`font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}> · {subtitle}</span>}</span>
            <span className="text-base leading-none rotate-180">⌄</span>
          </button>
        ) : (
          <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Timeline</h3>
        )}
        {/* wraps rather than overflowing — six ranges is a tight fit on a phone */}
        <div className="flex flex-wrap justify-end gap-1">
          {CHART_RANGES.map(d => (
            <button key={d} onClick={() => { setPinnedWindow(d); setDetail(null); }} className={btn(d)}>
              {chartRangeLabel(d)}
            </button>
          ))}
        </div>
      </div>

      {/* Pan controls: the ranges only ever measure back from now, so without
          these anything older than the widest range is unreachable. Hidden
          entirely when there's nothing older to reach and we're not already
          panned — otherwise it's just two dead buttons under every chart. */}
      {(canGoOlder || isPanned) && (
      <div className="flex items-center justify-center gap-2 mb-3">
        <button
          onClick={goOlder}
          disabled={!canGoOlder}
          aria-label="Show older"
          className={`px-2 py-1 rounded text-xs font-medium transition ${
            canGoOlder
              ? darkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              : 'opacity-30 cursor-not-allowed ' + (darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700')
          }`}>
          ‹ Older
        </button>
        <span className={`text-xs tabular-nums ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {isPanned
            ? `${new Date(tStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(tEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
            : `last ${chartRangeLabel(windowDays)}`}
        </span>
        <button
          onClick={goNewer}
          disabled={!isPanned}
          aria-label="Show newer"
          className={`px-2 py-1 rounded text-xs font-medium transition ${
            isPanned
              ? darkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              : 'opacity-30 cursor-not-allowed ' + (darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700')
          }`}>
          Newer ›
        </button>
        {isPanned && (
          <button
            onClick={() => { setPannedEnd(null); setDetail(null); }}
            className={`px-2 py-1 rounded text-xs font-medium transition ${
              darkMode ? 'bg-indigo-700 text-white hover:bg-indigo-600' : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'
            }`}>
            Now
          </button>
        )}
      </div>
      )}

      {empty ? (
        <p className={`text-sm text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {canGoOlder
            ? 'Nothing in this window — there are older records, use ‹ Older.'
            : 'No readings in this window. Log a temperature or widen the range.'}
        </p>
      ) : (
        <>
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ touchAction: 'manipulation' }} onClick={() => setDetail(null)}>
            {degTicks.map(d => (
              <g key={d}>
                <line x1={L} y1={yOf(d)} x2={R} y2={yOf(d)} stroke={grid} strokeWidth="1" />
                <text x={L - 4} y={yOf(d) + 3} textAnchor="end" fontSize="10" fill={axis}>{d}°</text>
              </g>
            ))}

            {xTicks.map((t, i) => (
              <line key={`xg${i}`} x1={xOf(t)} y1={T} x2={xOf(t)} y2={B} stroke={grid} strokeWidth="1" opacity="0.7" />
            ))}

            <line x1={L} y1={yOf(38)} x2={R} y2={yOf(38)} stroke={FEVER} strokeWidth="1.5" strokeDasharray="5 4" />
            <text x={R} y={yOf(38) - 3} textAnchor="end" fontSize="10" fill={FEVER}>fever</text>

            {medPts.map((p, i) => {
              const st = medStyle(p.raw.medication);
              return <line key={`g${i}`} x1={xOf(p.t)} y1={T} x2={xOf(p.t)} y2={B} stroke={st.c} strokeWidth="1.5" strokeDasharray="3 3" opacity="0.4" />;
            })}

            {pts.length > 1 && <polyline points={line} fill="none" stroke={TEMP} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
            {pts.map((p, i) => (
              <circle key={`t${i}`} cx={xOf(p.t)} cy={yOf(p.v)} r="4" fill={TEMP} style={{ cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); setDetail(`${p.v.toFixed(1)} °C · ${formatTimestamp(p.raw.timestamp)}`); }} />
            ))}

            {medGroups.map((g, gi) => {
              const onClk = (e) => { e.stopPropagation(); setDetail(medDetailAt(g.items[0])); };
              return (
                <g key={`g${gi}`}>
                  {g.items.map((p, i) => {
                    const st = medStyle(p.raw.medication);
                    const x = xOf(p.t);
                    const common = { fill: st.c, style: { cursor: 'pointer' }, onClick: onClk };
                    if (st.s === 'diamond') return <polygon key={`m${i}`} points={`${x},${B-6} ${x+6},${B} ${x},${B+6} ${x-6},${B}`} {...common} />;
                    if (st.s === 'square') return <rect key={`m${i}`} x={x-5} y={B-5} width="10" height="10" {...common} />;
                    return <circle key={`m${i}`} cx={x} cy={B} r="5.5" {...common} />;
                  })}
                  {/* A pile of markers is indistinguishable from a single one, so
                      say how many are stacked there. Sits above the marker, clear
                      of the x-axis labels below the baseline, and flips to its
                      left when there's no room on the right — a stack at "now"
                      sits hard against the right edge, which is exactly where
                      stacks most often are. */}
                  {g.items.length > 1 && (() => {
                    const bx = g.x + 8 + BADGE_R <= W ? g.x + 8 : g.x - 8;
                    return (
                      <g style={{ cursor: 'pointer' }} onClick={onClk}>
                        <circle cx={bx} cy={B - 11} r={BADGE_R}
                          fill={darkMode ? '#1f2937' : '#ffffff'} stroke={axis} strokeWidth="1" />
                        <text x={bx} y={B - 8.5} textAnchor="middle"
                          fontSize="8.5" fontWeight="bold" fill={axis}>{g.items.length}</text>
                      </g>
                    );
                  })()}
                </g>
              );
            })}

            {xTicks.map((t, i) => (
              <text key={`x${i}`} x={xOf(t)} y={B + 16} textAnchor={tickAnchor(xOf(t))} fontSize="10" fill={axis}>{tickLabel(t)}</text>
            ))}
          </svg>

          {/* min-height, not a fixed one: the line still reserves its space so
              the legend doesn't jump when a detail appears, but a stack of
              medicines is long enough to wrap and must not overrun the legend. */}
          <p className={`text-xs text-center mt-1 min-h-[1rem] ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {detail || (pts.length || medPts.length ? 'Tap a point or marker for details' : '')}
          </p>

          <div className={`flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5" style={{ background: TEMP }} />Temp</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5" style={{ background: FEVER }} />Fever 38°</span>
            <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: MED_COLORS.paracetamol.hex }} />Paracetamol</span>
            <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rotate-45" style={{ background: MED_COLORS.ibuprofen.hex }} />Ibuprofen</span>
            {hasOther && <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5" style={{ background: darkMode ? OTHER_MED_COLOR.hexDark : OTHER_MED_COLOR.hex }} />Other</span>}
          </div>
        </>
      )}
    </div>
  );
}
